import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { fmtMoney } from '../../shared/format'
import { monthShort } from '../ui'
import { AXIS_TICK, axisMoney, chartColor, SERIES, TOOLTIP_STYLE } from '../visuals'
import { CategoryDot } from './FinanceBits'

/** Grafik yalnız görüntüleme için sayıya çevirir; tutarlar sunucudan string gelir. */
const n = (v: string) => Number(v)
const moneyTip = (v: unknown) => fmtMoney(String(v))

export function IncomeExpenseChart({ data, incomeLabel, expenseLabel }: { data: { month: string; income: string; expense: string }[]; incomeLabel: string; expenseLabel: string }) {
  const rows = data.map((d) => ({ name: monthShort(d.month), income: n(d.income), expense: n(d.expense) }))
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ left: 0, right: 8, top: 8 }} barGap={4}>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={44} tickFormatter={axisMoney} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--muted)' }} formatter={moneyTip} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Bar dataKey="income" name={incomeLabel} fill={SERIES.income} radius={[4, 4, 0, 0]} maxBarSize={28} />
          <Bar dataKey="expense" name={expenseLabel} fill={SERIES.expense} radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

type Slice = { id: string; name: string; amount: string; pct: number; color: number }

export function CategoryDonut({ slices, total, centerLabel }: { slices: Slice[]; total: string; centerLabel: string }) {
  const { t } = useTranslation()
  if (!slices.length) return <p className="py-10 text-center text-sm text-muted-foreground">{t('finance.noData')}</p>
  return (
    <div className="flex flex-col items-center gap-4 @container sm:flex-row">
      <div className="relative size-44 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices.map((s) => ({ ...s, value: n(s.amount) }))} dataKey="value" nameKey="name" innerRadius="64%" outerRadius="100%" paddingAngle={2} stroke="var(--card)" strokeWidth={2}>
              {slices.map((s) => (
                <Cell key={s.id} fill={chartColor(s.color)} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={moneyTip} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-base font-semibold tabular-nums">{fmtMoney(total)}</span>
          <span className="text-[11px] text-muted-foreground">{centerLabel}</span>
        </div>
      </div>
      <ul className="w-full min-w-0 space-y-2">
        {slices.map((s) => (
          <li key={s.id} className="flex items-center gap-2 text-sm">
            <CategoryDot color={s.color} />
            <span className="min-w-0 flex-1 truncate">{s.name}</span>
            <span className="tabular-nums">{fmtMoney(s.amount)}</span>
            <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">%{s.pct.toLocaleString('tr-TR')}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function VatChart({ data }: { data: { month: string; output: string; input: string; payable: string }[] }) {
  const { t } = useTranslation()
  const rows = data.map((d) => ({ name: monthShort(d.month), output: n(d.output), input: n(d.input), payable: n(d.payable) }))
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ left: 0, right: 8, top: 8 }} barGap={4}>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={44} tickFormatter={axisMoney} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--muted)' }} formatter={moneyTip} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Bar dataKey="output" name={t('finance.tax.vatOutput')} fill={SERIES.primary} radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar dataKey="input" name={t('finance.tax.vatInput')} fill={SERIES.secondary} radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Line dataKey="payable" name={t('finance.tax.vatPayable')} stroke="var(--foreground)" strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: 'var(--card)' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

export function ReviewDonut({ approved, pending, problem }: { approved: number; pending: number; problem: number }) {
  const { t } = useTranslation()
  const total = approved + pending + problem
  const items = [
    { key: 'approved', value: approved, color: 'var(--success)' },
    { key: 'pending', value: pending, color: 'var(--warning)' },
    { key: 'problem', value: problem, color: 'var(--destructive)' },
  ]
  const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0)
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <div className="relative size-36 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={items} dataKey="value" nameKey="key" innerRadius="68%" outerRadius="100%" stroke="var(--card)" strokeWidth={2}>
              {items.map((i) => (
                <Cell key={i.key} fill={i.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-semibold tabular-nums">%{pct(approved)}</span>
          <span className="text-[11px] text-muted-foreground">{t('finance.tax.review.approved')}</span>
        </div>
      </div>
      <ul className="w-full space-y-2 text-sm">
        {items.map((i) => (
          <li key={i.key} className="flex items-center gap-2">
            <span className="size-2.5 rounded-full" style={{ background: i.color }} aria-hidden />
            <span className="flex-1">{t(`finance.tax.review.${i.key}`)}</span>
            <span className="tabular-nums text-muted-foreground">
              {i.value} · %{pct(i.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function CashFlowChart({ data }: { data: { month: string; inflow: string; outflow: string; closing: string }[] }) {
  const { t } = useTranslation()
  const rows = data.map((d) => ({ name: monthShort(d.month), inflow: n(d.inflow), outflow: n(d.outflow), closing: n(d.closing) }))
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ left: 0, right: 8, top: 8 }} barGap={4}>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={48} tickFormatter={axisMoney} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--muted)' }} formatter={moneyTip} />
          <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
          <Bar dataKey="inflow" name={t('finance.cash.inflow')} fill={SERIES.income} radius={[4, 4, 0, 0]} maxBarSize={26} />
          <Bar dataKey="outflow" name={t('finance.cash.outflow')} fill={SERIES.expense} radius={[4, 4, 0, 0]} maxBarSize={26} />
          <Line dataKey="closing" name={t('finance.cash.closing')} stroke={SERIES.primary} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: 'var(--card)' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
